// src/contexts/MessagingContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  getOrCreateConversation, 
  sendMessage, 
  getConversationMessages, 
  getUserConversations,
  markConversationAsRead,
  getUnreadMessageCount,
  prepareConversation
} from '../services/enhancedFirestore';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

// Create Context
const MessagingContext = createContext();

// Messaging Provider Component
export const MessagingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Set up real-time listener for conversations
  useEffect(() => {
    let unsubscribe = null;
    
    const setupConversationListener = async () => {
      if (!currentUser) {
        setConversations([]);
        setActiveConversation(null);
        setMessages([]);
        setUnreadCount(0);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        console.log("Setting up real-time conversation listener for user:", currentUser.uid);
        
        const conversationsRef = collection(db, "conversations");
        const q = query(
          conversationsRef, 
          where("participants", "array-contains", currentUser.uid)
        );
        
        // Create real-time listener
        unsubscribe = onSnapshot(q, async (querySnapshot) => {
          const userConversations = [];
          
          querySnapshot.forEach((doc) => {
            userConversations.push({ id: doc.id, ...doc.data() });
          });
          
          // Sort conversations by most recent message
          userConversations.sort((a, b) => {
            const aTime = a.updatedAt ? new Date(a.updatedAt.seconds * 1000) : new Date(0);
            const bTime = b.updatedAt ? new Date(b.updatedAt.seconds * 1000) : new Date(0);
            return bTime - aTime;
          });
          
          console.log(`Real-time update: ${userConversations.length} conversations received`);
          setConversations(userConversations);
          
          // Update active conversation if it's in the list (might have been updated)
          if (activeConversation) {
            const updated = userConversations.find(c => c.id === activeConversation.id);
            if (updated && JSON.stringify(updated) !== JSON.stringify(activeConversation)) {
              setActiveConversation(updated);
            }
          }
          
          // Get unread message count with error handling
          try {
            const count = await getUnreadMessageCount(currentUser.uid);
            setUnreadCount(count);
          } catch (err) {
            console.warn('Error getting unread count:', err.message);
            // Don't set error state for non-critical failures
          }
          
          setLoading(false);
        }, (error) => {
          console.error('Error in real-time conversation listener:', error);
          
          // Handle different types of Firebase errors gracefully
          if (error.code === 'permission-denied') {
            setError('Permission denied: Unable to access conversations. Please check your account permissions.');
          } else if (error.code === 'unavailable') {
            setError('Service temporarily unavailable. Please try again later.');
          } else {
          setError('Failed to load conversations: ' + error.message);
          }
          
          setLoading(false);
        });
      } catch (error) {
        console.error('Error setting up conversation listener:', error);
        setError('Failed to load conversations: ' + error.message);
        setLoading(false);
      }
    };
    
    setupConversationListener();
    
    // Clean up listener when component unmounts
    return () => {
      if (unsubscribe) {
        console.log('Cleaning up conversation listener');
        unsubscribe();
      }
    };
  }, [currentUser]);
  
  // Set up real-time listener for messages in the active conversation
  useEffect(() => {
    let unsubscribe = null;
    
    const setupMessageListener = async () => {
      if (!activeConversation || !currentUser) {
        setMessages([]);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Get conversation data first for member information
        const conversationRef = doc(db, "conversations", activeConversation.id);
        const conversationSnap = await getDoc(conversationRef);
        const conversationData = conversationSnap.exists() ? conversationSnap.data() : null;
        const memberInfo = conversationData?.members || {};
        
        // Mark conversation as read with error handling
        if (currentUser) {
          try {
          await markConversationAsRead(activeConversation.id, currentUser.uid);
          
          // Update unread count
          const count = await getUnreadMessageCount(currentUser.uid);
          setUnreadCount(count);
          } catch (err) {
            console.warn('Error marking conversation as read:', err.message);
            // Don't fail the entire operation for this
          }
        }
        
        // Set up real-time listener for messages
        console.log('Setting up real-time message listener for conversation:', activeConversation.id);
        
        const messagesRef = collection(db, "messages");
        const q = query(
          messagesRef,
          where("conversationId", "==", activeConversation.id),
          orderBy("createdAt", "asc")
        );
        
        // Create a real-time listener
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const newMessages = [];
          
          querySnapshot.forEach((doc) => {
            const messageData = doc.data();
            
            // Enhance message with sender info if available
            if (messageData.senderId && memberInfo[messageData.senderId]) {
              newMessages.push({
                id: doc.id,
                ...messageData,
                // Add sender name directly to message
                senderName: memberInfo[messageData.senderId].displayName || null,
                // Include full members object for reference
                conversationMembers: memberInfo
              });
            } else {
              newMessages.push({ id: doc.id, ...messageData });
            }
          });
          
          console.log(`Real-time update: ${newMessages.length} messages received`);
          
          // Remove any optimistic messages that have been replaced by real ones
          const optimisticIds = newMessages
            .filter(msg => msg.isOptimistic)
            .map(msg => msg.tempId);
          
          const finalMessages = newMessages.filter(msg => 
            !msg.isOptimistic || !optimisticIds.includes(msg.tempId)
          );
          
          setMessages(finalMessages);
          setLoading(false);
        }, (error) => {
          console.error('Error in real-time message listener:', error);
          
          // Handle different types of Firebase errors gracefully
          if (error.code === 'permission-denied') {
            setError('Permission denied: Unable to access messages. Please check your account permissions.');
          } else if (error.code === 'unavailable') {
            setError('Service temporarily unavailable. Please try again later.');
          } else if (error.code === 'failed-precondition') {
            setError('Database index required. Please contact support.');
          } else {
          setError('Failed to load messages: ' + error.message);
          }
          
          setLoading(false);
        });
      } catch (error) {
        console.error('Error setting up message listener:', error);
        setError('Failed to load messages: ' + error.message);
        setLoading(false);
      }
    };
    
    setupMessageListener();
    
    // Clean up listener when component unmounts or conversation changes
    return () => {
      if (unsubscribe) {
        console.log('Cleaning up message listener');
        unsubscribe();
      }
    };
  }, [activeConversation, currentUser]);
  
  // Start or continue a conversation
  const startConversation = useCallback(async (userId) => {
    setError(null);
    
    // Enhanced validation
    if (!currentUser || !currentUser.uid) {
      const errorMsg = 'Cannot start conversation: You are not logged in';
      console.error(errorMsg);
      setError(errorMsg);
      return null;
    }
    
    if (!userId) {
      const errorMsg = 'Cannot start conversation: No recipient specified';
      console.error(errorMsg);
      setError(errorMsg);
      return null;
    }
    
    // Strict equality check with enhanced logging
    if (userId === currentUser.uid) {
      const errorMsg = 'You cannot start a conversation with yourself.';
      console.error('Self-conversation attempt detected:', {
        currentUserId: currentUser.uid,
        targetUserId: userId,
        areEqual: currentUser.uid === userId
      });
      setError(errorMsg);
      return null;
    }
    
    try {
      console.log("Starting conversation with user:", userId, "Current user:", currentUser.uid);
      
      // Ensure both users exist in Firestore
      await prepareConversation(currentUser.uid, userId);
      
      // Get or create conversation
      const conversation = await getOrCreateConversation(currentUser.uid, userId);
      console.log("Conversation created/found:", conversation);
      
      // Verify participants are not the same person (final safety check)
      if (conversation.participants) {
        const uniqueParticipants = new Set(conversation.participants);
        if (uniqueParticipants.size !== conversation.participants.length) {
          const errorMsg = 'Invalid conversation: Contains duplicate participants';
          console.error(errorMsg, conversation.participants);
          setError(errorMsg);
          return null;
        }
      }
      
      // Check if it's already in our list
      const existingIndex = conversations.findIndex(conv => conv.id === conversation.id);
      
      if (existingIndex === -1) {
        // Add to conversations list
        setConversations(prev => [conversation, ...prev]);
      }
      
      // Set as active conversation
      setActiveConversation(conversation);
      
      return conversation;
    } catch (error) {
      console.error('Detailed error in startConversation:', error);
      console.error('Error stack:', error.stack);
      
      // Enhanced error handling for Firebase permission errors
      if (error.code === 'permission-denied') {
        setError('Permission denied: You do not have access to create or view this conversation. Please check your account permissions.');
      } else {
        setError(`Failed to start conversation: ${error.message}`);
      }
      return null;
    }
  }, [currentUser, conversations]);
  
  // Send a message with optimistic updates
  const sendNewMessage = useCallback(async (text) => {
    if (!currentUser || !activeConversation) return false;
    
    try {
      // Create optimistic message for immediate UI feedback
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        tempId: tempId, // To track this message
        conversationId: activeConversation.id,
        senderId: currentUser.uid,
        text: text,
        createdAt: new Date(), // Use JavaScript Date for immediate display
        participants: activeConversation.participants,
        read: false,
        // Include sender information if available
        senderName: currentUser.displayName || null,
        // Add flag to identify this as an optimistic message
        isOptimistic: true
      };
      
      // Add to messages state immediately
      setMessages(prev => [...prev, optimisticMessage]);
      
      // Now send the actual message to Firestore
      await sendMessage(activeConversation.id, currentUser.uid, text);
      
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message: ' + error.message);
      
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      
      return false;
    }
  }, [currentUser, activeConversation]);
  
  // Select a conversation
  const selectConversation = useCallback(async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    
    if (conversation) {
      setActiveConversation(conversation);
      
      // Mark as read with error handling
      if (currentUser) {
        try {
        await markConversationAsRead(conversationId, currentUser.uid);
        
        // Update unread count
        const count = await getUnreadMessageCount(currentUser.uid);
        setUnreadCount(count);
        } catch (err) {
          console.warn('Error marking conversation as read:', err.message);
          // Don't fail conversation selection for this
        }
      }
      
      return true;
    }
    
    return false;
  }, [conversations, currentUser]);
  
  // Refresh conversations
  const refreshConversations = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const userConversations = await getUserConversations(currentUser.uid);
      setConversations(userConversations);
      
      // Get unread count
      const count = await getUnreadMessageCount(currentUser.uid);
      setUnreadCount(count);
      
      // Refresh messages if active conversation
      if (activeConversation) {
        const conversationMessages = await getConversationMessages(activeConversation.id);
        setMessages(conversationMessages);
      }
      
    } catch (error) {
      console.error('Error refreshing conversations:', error);
      setError('Failed to refresh conversations');
    } finally {
      setLoading(false);
    }
  }, [currentUser, activeConversation]);
  
  // Context value with memoization
  const value = useMemo(() => ({
    conversations,
    activeConversation,
    messages,
    unreadCount,
    loading,
    error,
    startConversation,
    sendMessage: sendNewMessage,
    selectConversation,
    refreshConversations
  }), [
    conversations,
    activeConversation,
    messages,
    unreadCount,
    loading,
    error,
    startConversation,
    sendNewMessage,
    selectConversation,
    refreshConversations
  ]);
  
  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

// Custom hook for using the messaging context
export const useMessaging = () => {
  return useContext(MessagingContext);
};

export default MessagingContext;